import { get } from 'lodash';
import React, { useCallback, useState } from 'react';
import Axios from 'axios';
import { toast } from 'react-toastify';
import { Ball, Card } from '../../../elements';
import { Modal } from '../../../components';
import { acceptedFileTypes, describeAcceptedFileTypes } from '../../../components/acceptedFileTypes';
import { handleDocumentUpload } from '../../../services/upload_service';
import { API_URL } from '../../../../lib/const';
import { IDocument } from '../../common/types';
import { ICoordinationDocumentGroup } from '../types';
import { uploadCoordinationDocument } from '../services/transferApplicationService';

type Props = {
  token: string,
  group: ICoordinationDocumentGroup,
  canUpload: boolean,
  onUploaded: () => Promise<void> | void,
};

type ViewerState = {
  visible: boolean,
  title: string,
  src: string,
};

const CoordinationDocumentCard = ({ token, group, canUpload, onUploaded }: Props) => {
  const [pendingDocument, setPendingDocument] = useState<IDocument>();
  const [justification, setJustification] = useState('');
  const [uploadingFile, setUploadingFile] = useState(false);
  const [saving, setSaving] = useState(false);
  const [viewer, setViewer] = useState<ViewerState>({
    visible: false,
    title: '',
    src: '',
  });

  const openDocument = useCallback(async (document: IDocument) => {
    try {
      const endpoint = document.url || `${API_URL}/uploads/${document.key}`;
      const result = await Axios.get(endpoint);
      setViewer({
        visible: true,
        title: document.name,
        src: get(result, 'data.url', ''),
      });
    } catch (error) {
      toast.error('Não foi possível abrir o documento.');
    }
  }, []);

  const selectFile = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = get(event, 'target.files.0');
    if (!file || !group.document_type) return;

    setUploadingFile(true);
    try {
      const uploaded = await handleDocumentUpload(file, group.document_type.id);
      setPendingDocument({
        key: uploaded.key,
        name: file.name,
        document_type: group.document_type.id,
        approved: null,
        url: `${API_URL}/uploads/${uploaded.key}`,
      });
    } catch (error) {
      setPendingDocument(undefined);
    } finally {
      setUploadingFile(false);
    }
  }, [group.document_type]);

  const submit = useCallback(async () => {
    if (!pendingDocument) return;

    setSaving(true);
    try {
      await uploadCoordinationDocument(token, group.kind, pendingDocument, justification);
      setPendingDocument(undefined);
      setJustification('');
      await onUploaded();
      toast.success(`${group.title} enviado com sucesso.`);
    } catch (error) {
      const message = get(error, 'response.data.non_field_errors.0');
      toast.error(message || `Não foi possível enviar ${group.title}.`);
    } finally {
      setSaving(false);
    }
  }, [group.kind, group.title, justification, onUploaded, pendingDocument, token]);

  return (
    <div className="col-sm-12 col-lg-6 mt-3">
      <Card title={group.title} className="shadow">
        <div className="p-3">
          <h4 className="mb-2">Versão vigente</h4>
          {group.current_document ? (
            <button
              className="btn btn-link p-0"
              type="button"
              onClick={() => openDocument(group.current_document as IDocument)}
            >
              {group.current_document.name}
            </button>
          ) : <p>Nenhum documento enviado.</p>}

          {canUpload && group.document_type && (
            <div className="documents-base d-block mt-3">
              <div className="documents-upload p-0">
                <div className="btn-none">
                  <input
                    type="file"
                    className="input-file"
                    accept={acceptedFileTypes(group.document_type.validations)}
                    disabled={uploadingFile || saving}
                    onChange={selectFile}
                  />
                  <Ball icon="upload" small bgColor="white" iconWidth="18" />
                </div>
                <div className="documents-upload-description">
                  <p>{uploadingFile ? 'Enviando arquivo...' : 'Selecionar nova versão'}</p>
                  <p>
                    Tipos de arquivo aceitos: {describeAcceptedFileTypes(group.document_type.validations)}
                  </p>
                </div>
              </div>
              {pendingDocument && <p className="mt-2">Arquivo selecionado: {pendingDocument.name}</p>}
              <div className="form-group mt-2">
                <label htmlFor={`justification-${group.kind}`}>Justificativa (opcional)</label>
                <textarea
                  id={`justification-${group.kind}`}
                  className="form-control"
                  rows={2}
                  disabled={saving}
                  value={justification}
                  onChange={(event) => setJustification(event.target.value)}
                  placeholder="Explique o motivo da nova versão, se for o caso."
                />
              </div>
              <button
                className="btn btn-primary mt-2"
                type="button"
                disabled={!pendingDocument || uploadingFile || saving}
                onClick={submit}
              >
                {saving ? 'Salvando...' : (
                  group.current_document ? 'Enviar nova versão' : 'Enviar documento'
                )}
              </button>
            </div>
          )}

          <h4 className="mt-4 mb-2">Histórico de versões</h4>
          {group.versions.length === 0 ? <p>Nenhuma versão registrada.</p> : (
            <div>
              {group.versions.map((item, index) => (
                <div className="border-top py-2" key={item.id}>
                  <button
                    className="btn btn-link p-0 text-left"
                    type="button"
                    onClick={() => openDocument(item.document)}
                  >
                    Versão {item.version}{index === 0 ? ' (atual)' : ''}: {item.document.name}
                  </button>
                  <small className="d-block">
                    Enviado por {item.uploaded_by ? item.uploaded_by.display_name : 'Usuário não identificado'} em {item.uploaded_at}
                  </small>
                  {item.justification && (
                    <small className="d-block">
                      <strong>Justificativa:</strong> {item.justification}
                    </small>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </Card>
      <Modal
        visible={viewer.visible}
        onClose={() => setViewer({ visible: false, title: '', src: '' })}
        title={viewer.title}
        src={viewer.src}
      />
    </div>
  );
};

export default CoordinationDocumentCard;
